import React, { ForwardedRef } from 'react';

import { Platform, ScrollView, findNodeHandle } from 'react-native';

import { useIsDesktopBreakpoint } from 'hooks/use-media-query';

type Props<Scrollable extends ScrollView = ScrollView> = {
  cursor?: string;
  outerRef?: ForwardedRef<Scrollable>;
};

const mergeRefs = (...refs: any) => {
  return (element: any) => {
    refs.forEach((ref: any) => {
      if (!ref) {
        return;
      }
      ref.current = element;
    });
  };
};

// @see https://github.com/necolas/react-native-web/issues/2089

export function useDraggableScroll<Scrollable extends ScrollView = ScrollView>({
  outerRef,
  cursor = 'grab',
}: Props<Scrollable> = {}) {
  const isDesktop = useIsDesktopBreakpoint();
  const ref = React.useRef<Scrollable>(null);

  React.useEffect(() => {
    if (Platform.OS !== 'web' || !isDesktop || !ref.current) {
      return;
    }
    const slider = (findNodeHandle(ref.current) as unknown) as HTMLDivElement;

    if (!slider) {
      return;
    }

    let isDragging = false;
    let isMouseDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const mouseDown = (e: MouseEvent) => {
      isMouseDown = true;
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;

      slider.style.cursor = cursor;
    };

    const mouseUp = () => {
      if (isDragging) {
        slider.addEventListener('click', e => e.stopPropagation(), { once: true });
      }

      isMouseDown = false;
      isDragging = false;
      slider.style.cursor = 'default';
    };

    const mouseMove = (e: MouseEvent) => {
      if (!isMouseDown) {
        return;
      }

      // Require n pixels momement before start of drag (3 in this case )
      const x = e.pageX - slider.offsetLeft;
      if (Math.abs(x - startX) < 3) {
        return;
      }

      isDragging = true;
      e.preventDefault();
      const walk = x - startX;
      slider.scrollLeft = scrollLeft - walk;
    };

    slider.addEventListener('mousedown', mouseDown);
    window.addEventListener('mouseup', mouseUp);
    window.addEventListener('mousemove', mouseMove);

    return () => {
      slider.removeEventListener('mousedown', mouseDown);
      window.removeEventListener('mouseup', mouseUp);
      window.removeEventListener('mousemove', mouseMove);
    };
  }, [cursor, isDesktop]);

  const refs = React.useMemo(() => mergeRefs(ref, outerRef), [ref, outerRef]);

  return refs;
}
